import React, { useState, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Button, Tabs, Pagination, Select, Input, Tag } from 'antd';
import { FaCodeBranch, FaPlusCircle } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';
import '../../assets/styles/JectHubChallenges.css';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const projectsData = {
  "AI & Machine Learning": [
    { title: "AI Chatbot", description: "A chatbot that uses NLP to interact with users.", difficulty: "Intermediate", tech: "Python, TensorFlow", country: "Kenya" },
    { title: "Image Recognition", description: "A model to classify objects in images.", difficulty: "Beginner", tech: "PyTorch, OpenCV", country: "Kenya" },
    { title: "Fraud Detection", description: "AI for detecting fraudulent transactions.", difficulty: "Advanced", tech: "Scikit-learn, Pandas", country: "Kenya" },
    { title: "AI-Based Healthcare Diagnosis", description: "AI to assist in diagnosing diseases from medical images.", difficulty: "Advanced", tech: "TensorFlow, Keras", country: "Kenya" },
    { title: "Voice Recognition for Local Languages", description: "A model to recognize local languages spoken in rural areas.", difficulty: "Intermediate", tech: "Python, Keras", country: "Kenya" },
    { title: "AI for Agriculture", description: "Using AI to detect crop diseases and optimize farming practices.", difficulty: "Intermediate", tech: "TensorFlow, OpenCV", country: "Kenya" },
    { title: "Smart Traffic Management", description: "AI-powered traffic control to reduce congestion.", difficulty: "Advanced", tech: "Python, OpenCV", country: "Kenya" },
    { title: "AI-Powered E-Learning Platform", description: "Personalized learning experiences using AI.", difficulty: "Intermediate", tech: "React, TensorFlow", country: "Kenya" },
    { title: "AI for Wildlife Conservation", description: "Using AI to monitor and protect wildlife.", difficulty: "Advanced", tech: "TensorFlow, Keras", country: "Tanzania" },
    { title: "AI for Climate Change Analysis", description: "Analyzing climate data using AI models.", difficulty: "Intermediate", tech: "Python, Scikit-learn", country: "South Africa" },
    { title: "AI for Financial Forecasting", description: "Predicting financial trends using AI.", difficulty: "Advanced", tech: "TensorFlow, Pandas", country: "Nigeria" },
    { title: "AI for Customer Service Automation", description: "Automating customer service interactions using AI.", difficulty: "Intermediate", tech: "Python, TensorFlow", country: "Ghana" }
  ],
  "Cybersecurity": [
    { title: "CTF Challenges", description: "Solve security vulnerabilities in applications.", difficulty: "Advanced", tech: "Kali Linux, Burp Suite", country: "Kenya" },
    { title: "Network Security Scanner", description: "A tool to scan for network vulnerabilities.", difficulty: "Intermediate", tech: "Python, Nmap", country: "Kenya" },
    { title: "Ransomware Detection", description: "Detect ransomware attacks using machine learning.", difficulty: "Advanced", tech: "TensorFlow, Keras", country: "Kenya" },
    { title: "Web Application Security Testing", description: "Test security vulnerabilities in web applications.", difficulty: "Intermediate", tech: "OWASP ZAP, Burp Suite", country: "Kenya" },
    { title: "AI-Based Phishing Detection", description: "Using AI to detect and prevent phishing attacks.", difficulty: "Advanced", tech: "Python, TensorFlow", country: "Kenya" },
    { title: "Security Incident Response System", description: "Develop a system to respond to security incidents in real-time.", difficulty: "Advanced", tech: "Python, Kali Linux", country: "Nigeria" },
    { title: "Cybersecurity Training Platform", description: "Create a platform for cybersecurity training and certification.", difficulty: "Intermediate", tech: "Python, Django", country: "South Africa" },
    { title: "Blockchain Security Analysis", description: "Analyze blockchain networks for security vulnerabilities.", difficulty: "Advanced", tech: "Python, Ethereum", country: "Tanzania" },
    { title: "AI for Cyber Threat Intelligence", description: "Develop AI models to gather and analyze cyber threat intelligence.", difficulty: "Intermediate", tech: "Python, TensorFlow", country: "Ghana" },
    { title: "Secure Communication System", description: "Build a secure communication system using encryption and AI.", difficulty: "Advanced", tech: "Python, OpenSSL", country: "Ethiopia" }
  ],
  "Data Science": [
    { title: "Customer Segmentation", description: "Analyze customer data for business insights.", difficulty: "Intermediate", tech: "Python, Pandas", country: "Kenya" },
    { title: "Stock Price Prediction", description: "Use ML models to predict stock trends.", difficulty: "Advanced", tech: "R, Scikit-learn", country: "Kenya" },
    { title: "Predicting African Poverty Levels", description: "Use data science models to predict poverty levels in African regions.", difficulty: "Intermediate", tech: "Python, Pandas", country: "Kenya" },
    { title: "Sentiment Analysis on Social Media", description: "Analyze social media sentiments about various African countries.", difficulty: "Intermediate", tech: "Python, NLTK", country: "Kenya" },
    { title: "E-commerce Recommendation System", description: "Build a recommendation engine for e-commerce websites.", difficulty: "Advanced", tech: "Python, Scikit-learn", country: "Kenya" },
    { title: "Healthcare Data Analysis", description: "Analyze healthcare data to improve patient outcomes.", difficulty: "Intermediate", tech: "Python, Pandas", country: "Nigeria" },
    { title: "Agricultural Yield Prediction", description: "Predict crop yields using historical data.", difficulty: "Advanced", tech: "Python, Scikit-learn", country: "South Africa" },
    { title: "Energy Consumption Forecasting", description: "Forecast energy consumption patterns.", difficulty: "Intermediate", tech: "Python, TensorFlow", country: "Tanzania" },
    { title: "Market Basket Analysis", description: "Analyze customer purchase patterns in retail.", difficulty: "Intermediate", tech: "Python, Pandas", country: "Ghana" },
    { title: "Climate Data Analysis", description: "Analyze climate data to predict weather patterns.", difficulty: "Advanced", tech: "Python, Scikit-learn", country: "Ethiopia" }
  ],
  "Open Source": [
    { title: "AI Research Paper", description: "Collaborate on a research paper on deep learning.", difficulty: "Advanced", tech: "TensorFlow, NLP", country: "Kenya" },
    { title: "AI API Development", description: "Build and deploy an API for AI models.", difficulty: "Intermediate", tech: "Flask, Docker", country: "Nigeria" },
    { title: "Open Data Platform for African Countries", description: "Create a platform to share open data on African countries for research and development.", difficulty: "Intermediate", tech: "Python, Django", country: "South Africa" },
    { title: "Open Source Cybersecurity Tools", description: "Build a suite of cybersecurity tools available for public use.", difficulty: "Advanced", tech: "Python, Kali Linux", country: "Uganda" },
    { title: "Open Source AI Framework", description: "Develop an open-source AI framework for educational purposes.", difficulty: "Advanced", tech: "Python, TensorFlow", country: "Kenya" },
    { title: "Open Source Data Visualization Tools", description: "Create open-source tools for data visualization.", difficulty: "Intermediate", tech: "Python, D3.js", country: "Nigeria" },
    { title: "Open Source Machine Learning Library", description: "Develop an open-source library for machine learning algorithms.", difficulty: "Advanced", tech: "Python, Scikit-learn", country: "South Africa" },
    { title: "Open Source Blockchain Platform", description: "Build an open-source blockchain platform for various applications.", difficulty: "Intermediate", tech: "Python, Ethereum", country: "Tanzania" },
    { title: "Open Source AI Education Platform", description: "Create an open-source platform for AI education and training.", difficulty: "Intermediate", tech: "Python, Flask", country: "Ghana" },
    { title: "Open Source Cybersecurity Training", description: "Develop open-source training materials for cybersecurity.", difficulty: "Advanced", tech: "Python, Kali Linux", country: "Ethiopia" }
  ]
};

const pageSize = 4;

const ProjectsList = () => {
  const [currentTab, setCurrentTab] = useState("AI & Machine Learning");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (key) => {
    setCurrentTab(key);
    setCurrentPage(1);
    setSelectedCountry('');
    setSearchQuery('');
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const currentProjects = projectsData[currentTab] || [];

  const filteredProjects = useMemo(() => {
    let filtered = currentProjects;
    if (selectedCountry) {
      filtered = filtered.filter((project) => project.country === selectedCountry);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.title.toLowerCase().includes(query) ||
          project.description.toLowerCase().includes(query)
      );
    }
    return filtered;
  }, [currentProjects, selectedCountry, searchQuery]);

  const paginatedProjects = useMemo(() => {
    return filteredProjects.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredProjects, currentPage]);

  const uniqueCountries = useMemo(
    () => [...new Set(currentProjects.map((project) => project.country))],
    [currentProjects]
  );

  return (
    <Layout>
      <Content style={{ padding: '60px', background: 'linear-gradient(135deg, #e6fffb, #fff7e6)' }}>
        <Title level={2} style={{ fontSize: '20px', display: 'flex', alignItems: 'center', color: '#13c2c2', marginBottom: '28px', fontWeight: '700' }}>
          <FaCodeBranch style={{ marginRight: '14px', fontSize: '20px', color: '#fa8c16' }} /> Projects
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#595959', marginBottom: '32px', maxWidth: '800px' }}>
          Collaborate on cutting-edge AI, Data Science, and Cybersecurity projects. Boost your skills and join a vibrant community!
        </Paragraph>
        <div style={{ marginBottom: '32px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            type="primary"
            icon={<FaPlusCircle style={{ marginRight: '8px' }} />}
            style={{
              background: 'linear-gradient(45deg, #52c41a, #389e0d)',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              height: '44px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #389e0d, #237804)';
              e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #52c41a, #389e0d)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
            }}
          >
            Create New Project
          </Button>
          <Input
            placeholder="Search projects by title or description"
            prefix={<SearchOutlined />}
            style={{ width: '340px', borderRadius: '8px', height: '44px', boxShadow: '0 3px 6px rgba(0,0,0,0.1)', border: 'none' }}
            onChange={handleSearchChange}
          />
          <Select
            allowClear
            placeholder="Filter by Country"
            style={{ width: '240px', borderRadius: '8px' }}
            onChange={handleCountryChange}
            dropdownStyle={{ borderRadius: '8px' }}
          >
            <Option value="">All Countries</Option>
            {uniqueCountries.map((country) => (
              <Option key={country} value={country}>
                {country}
              </Option>
            ))}
          </Select>
        </div>
        <Tabs
          defaultActiveKey="AI & Machine Learning"
          onChange={handleTabChange}
          className="custom-tabs"
          tabBarStyle={{ fontWeight: '700', color: '#13c2c2', borderBottom: '3px solid #13c2c2', marginBottom: '24px' }}
        >
          {Object.keys(projectsData).map((category) => (
            <TabPane tab={category} key={category}>
              <Row gutter={[24, 24]}>
                {paginatedProjects.length > 0 ? (
                  paginatedProjects.map((project, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card
                        hoverable
                        style={{
                          borderRadius: '16px',
                          overflow: 'hidden',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                          background: 'linear-gradient(135deg, #ffffff, #f0fafa)',
                          height: '340px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          padding: '20px',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-8px)';
                          e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #f0fafa, #e6fffb)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff, #f0fafa)';
                        }}
                      >
                        <div>
                          <Title level={4} style={{ marginBottom: '12px', color: '#1a1a1a', fontSize: '20px', fontWeight: '600' }}>
                            {project.title}
                          </Title>
                          <Paragraph
                            style={{ color: '#595959', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}
                            ellipsis={{ rows: 2, expandable: false }}
                          >
                            {project.description}
                          </Paragraph>
                          <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                            <strong style={{ color: '#13c2c2' }}>Difficulty:</strong>{' '}
                            <Tag
                              color={
                                project.difficulty === 'Beginner' ? '#52c41a' : project.difficulty === 'Intermediate' ? '#fa8c16' : '#f5222d'
                              }
                              style={{ borderRadius: '4px', fontWeight: 'bold' }}
                            >
                              {project.difficulty}
                            </Tag>
                          </Paragraph>
                          <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                            <strong style={{ color: '#13c2c2' }}>Tech Stack:</strong> {project.tech}
                          </Paragraph>
                          <Paragraph style={{ marginBottom: '0', color: '#888', fontSize: '13px' }}>
                            <strong style={{ color: '#13c2c2' }}>Country:</strong> {project.country}
                          </Paragraph>
                        </div>
                        <Button
                          type="primary"
                          href="/signin"
                          style={{
                            background: 'linear-gradient(45deg, #13c2c2, #08979c)',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            height: '44px',
                            width: '100%',
                            marginTop: '20px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(45deg, #08979c, #006d75)';
                            e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(45deg, #13c2c2, #08979c)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                          }}
                        >
                          Contribute
                        </Button>
                      </Card>
                    </Col>
                  ))
                ) : (
                  <Col span={24}>
                    <Paragraph style={{ textAlign: 'center', color: '#999', fontSize: '16px', marginTop: '24px' }}>
                      No projects match your filters. Try adjusting your search or country selection!
                    </Paragraph>
                  </Col>
                )}
              </Row>
              <Pagination
                current={currentPage}
                total={filteredProjects.length}
                pageSize={pageSize}
                onChange={setCurrentPage}
                style={{ marginTop: '32px', textAlign: 'center', fontSize: '16px' }}
                showSizeChanger={false}
              />
            </TabPane>
          ))}
        </Tabs>
        {/* Custom CSS */}
        <style jsx global>{`
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #13c2c2 !important;
          }
          .ant-tabs-ink-bar {
            background: #13c2c2 !important;
          }
          .ant-pagination-item-active {
            border-color: #fa8c16 !important;
          }
          .ant-pagination-item-active a {
            color: #fa8c16 !important;
          }
          .ant-select-selector {
            border: none !important;
            box-shadow: 0 3px 6px rgba(0,0,0,0.1) !important;
            height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-select-selection-placeholder {
            color: #888 !important;
          }
        `}</style>
      </Content>
    </Layout>
  );
};

export default ProjectsList;